(() => {
  const MODES = ['system', 'light', 'dark'];
  const modeLabels = { system: 'System', light: 'Light', dark: 'Dark' };

  function currentMode() {
    const mode = document.documentElement.dataset.colorMode;
    return MODES.includes(mode) ? mode : 'system';
  }

  function setMode(mode, persist = true) {
    const nextMode = MODES.includes(mode) ? mode : 'system';
    document.documentElement.dataset.colorMode = nextMode;
    if (persist) {
      try { localStorage.setItem('aurelia-color-mode', nextMode); } catch (error) {}
    }
    document.querySelectorAll('[data-theme-toggle]').forEach((button) => {
      const label = modeLabels[nextMode];
      button.dataset.mode = nextMode;
      button.setAttribute('aria-label', `Colour mode: ${label}. Activate for next mode.`);
      button.setAttribute('title', `Colour mode: ${label}`);
      const text = button.querySelector('[data-theme-label]');
      if (text) text.textContent = label;
    });
  }

  function initThemeToggles(root = document) {
    root.querySelectorAll('[data-theme-toggle]:not([data-initialized])').forEach((button) => {
      button.dataset.initialized = 'true';
      button.addEventListener('click', () => {
        const nextIndex = (MODES.indexOf(currentMode()) + 1) % MODES.length;
        setMode(MODES[nextIndex]);
      });
    });
    setMode(currentMode(), false);
  }

  function initStickyHeaders(root = document) {
    root.querySelectorAll('.site-header:not([data-sticky-initialized])').forEach((header) => {
      header.dataset.stickyInitialized = 'true';
      const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 16);
      updateHeader();
      window.addEventListener('scroll', updateHeader, { passive: true });
    });
  }

  function initSlideshows(root = document) {
    root.querySelectorAll('[data-slideshow]:not([data-initialized])').forEach((slideshow) => {
      slideshow.dataset.initialized = 'true';
      const slides = Array.from(slideshow.querySelectorAll('[data-slideshow-slide]'));
      const dots = Array.from(slideshow.querySelectorAll('[data-slideshow-dot]'));
      const autoplay = slideshow.dataset.autoplay === 'true';
      const interval = Math.max(3000, Number(slideshow.dataset.interval) || 5000);
      const fadeDuration = Math.max(0, parseFloat(getComputedStyle(slideshow).getPropertyValue('--slideshow-transition')) || 1000);
      let currentIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer = null;
      let editorPaused = false;
      const outgoingTimers = new WeakMap();

      window.requestAnimationFrame(() => slideshow.classList.add('is-ready'));
      if (slides.length < 2) return;

      const stopTimer = () => {
        if (timer) window.clearTimeout(timer);
        timer = null;
      };

      const schedule = () => {
        stopTimer();
        if (!autoplay || editorPaused || document.hidden) return;
        timer = window.setTimeout(() => showSlide(currentIndex + 1), interval);
      };

      const showSlide = (index, restart = true) => {
        const nextIndex = (index + slides.length) % slides.length;
        const previousSlide = slides[currentIndex];

        if (nextIndex !== currentIndex) {
          const existingTimer = outgoingTimers.get(previousSlide);
          if (existingTimer) window.clearTimeout(existingTimer);
          previousSlide.classList.add('is-outgoing');
          outgoingTimers.set(previousSlide, window.setTimeout(() => {
            previousSlide.classList.remove('is-outgoing');
            outgoingTimers.delete(previousSlide);
          }, fadeDuration));
        }

        currentIndex = nextIndex;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === currentIndex;
          if (active) {
            slide.classList.add('is-active');
            const outgoingTimer = outgoingTimers.get(slide);
            if (outgoingTimer) window.clearTimeout(outgoingTimer);
            outgoingTimers.delete(slide);
            slide.classList.remove('is-outgoing');
          } else {
            slide.classList.remove('is-active');
          }
          slide.setAttribute('aria-hidden', String(!active));
        });
        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === currentIndex;
          dot.classList.toggle('is-active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        if (restart) schedule();
      };

      slideshow.showSlide = showSlide;
      slideshow.addEventListener('shopify:block:select', (event) => {
        const selectedSlide = event.target.closest('[data-slideshow-slide]');
        const selectedIndex = slides.indexOf(selectedSlide);
        if (selectedIndex >= 0) {
          editorPaused = true;
          showSlide(selectedIndex);
        }
      });
      slideshow.addEventListener('shopify:block:deselect', () => {
        editorPaused = false;
        schedule();
      });
      document.addEventListener('visibilitychange', schedule);

      schedule();
    });
  }

  function initSortForms(root = document) {
    root.querySelectorAll('[data-sort-select]:not([data-initialized])').forEach((select) => {
      select.dataset.initialized = 'true';
      select.addEventListener('change', () => select.form.requestSubmit());
    });
  }

  function initMobileMenus(root = document) {
    root.querySelectorAll('[data-mobile-menu]:not([data-initialized])').forEach((details) => {
      details.dataset.initialized = 'true';
      const summary = details.querySelector('summary');
      const drawer = details.querySelector('.mobile-menu__drawer');
      const closeButton = details.querySelector('.mobile-menu__drawer-close');
      let returnFocus = false;

      const closeMenu = () => {
        returnFocus = true;
        details.open = false;
      };

      details.querySelectorAll('[data-mobile-menu-close]').forEach((button) => {
        button.addEventListener('click', closeMenu);
      });

      details.addEventListener('toggle', () => {
        if (summary) summary.setAttribute('aria-expanded', String(details.open));
        document.body.classList.toggle('menu-open', Boolean(document.querySelector('[data-mobile-menu][open]')));
        if (details.open && closeButton) {
          window.requestAnimationFrame(() => closeButton.focus());
        } else if (returnFocus && summary) {
          returnFocus = false;
          summary.focus();
        }
      });

      if (drawer) {
        drawer.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            closeMenu();
            return;
          }
          if (event.key !== 'Tab') return;
          const focusable = Array.from(drawer.querySelectorAll('a[href], button:not([disabled])'));
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        });
      }

      const desktopQuery = window.matchMedia('(min-width: 750px)');
      const closeAtDesktop = (event) => { if (event.matches && details.open) details.open = false; };
      if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', closeAtDesktop);
    });
  }

  function initProductForms(root = document) {
    root.querySelectorAll('[data-product-form]:not([data-initialized])').forEach((form) => {
      form.dataset.initialized = 'true';
      const section = form.closest('[data-product-url][data-section-id]');
      if (!section) return;

      form.querySelectorAll('[data-option-value]').forEach((input) => {
        input.addEventListener('change', async (event) => {
          const changedInput = event.currentTarget;
          const selectedIds = Array.from(form.querySelectorAll('[data-option-value]:checked'))
            .map((selected) => selected.dataset.optionValueId)
            .filter(Boolean);
          const productUrl = changedInput.dataset.productUrl || section.dataset.productUrl;
          const requestUrl = new URL(productUrl, window.location.origin);
          requestUrl.searchParams.set('option_values', selectedIds.join(','));
          requestUrl.searchParams.set('section_id', section.dataset.sectionId);

          const submit = form.querySelector('[data-product-submit]');
          const status = form.querySelector('[data-variant-status]');
          if (submit) submit.disabled = true;
          form.setAttribute('aria-busy', 'true');
          if (status) status.textContent = 'Updating selection…';

          try {
            const response = await fetch(requestUrl.toString(), { headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const markup = await response.text();
            const parsed = new DOMParser().parseFromString(markup, 'text/html');
            const nextSection = parsed.querySelector(`#ProductSection-${section.dataset.sectionId}`);
            if (!nextSection) throw new Error('Updated product section missing from response');

            section.replaceWith(nextSection);
            initialize(nextSection);
            if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();

            const publicUrl = new URL(productUrl, window.location.origin);
            publicUrl.searchParams.set('option_values', selectedIds.join(','));
            window.history.replaceState({}, '', publicUrl.toString());

            const nextInput = Array.from(nextSection.querySelectorAll('[data-option-value]'))
              .find((candidate) => candidate.dataset.optionValueId === changedInput.dataset.optionValueId);
            if (nextInput) nextInput.focus();
          } catch (error) {
            form.removeAttribute('aria-busy');
            if (status) status.textContent = 'This selection could not be updated. Refresh the page and try again.';
          }
        });
      });
    });
  }

  function initProductSliders(root = document) {
    root.querySelectorAll('.product-slider:not([data-drag-initialized])').forEach((slider) => {
      slider.dataset.dragInitialized = 'true';
      slider.querySelectorAll('img').forEach((image) => { image.draggable = false; });

      let activePointer = null;
      let startX = 0;
      let startScrollLeft = 0;
      let moved = false;
      let suppressClick = false;

      slider.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        activePointer = event.pointerId;
        startX = event.clientX;
        startScrollLeft = slider.scrollLeft;
        moved = false;
        slider.setPointerCapture(event.pointerId);
      });

      slider.addEventListener('pointermove', (event) => {
        if (event.pointerId !== activePointer) return;
        const distance = event.clientX - startX;
        if (Math.abs(distance) > 5) {
          moved = true;
          slider.classList.add('is-dragging');
          event.preventDefault();
        }
        slider.scrollLeft = startScrollLeft - distance;
      });

      const endDrag = (event) => {
        if (event.pointerId !== activePointer) return;
        if (slider.hasPointerCapture(event.pointerId)) slider.releasePointerCapture(event.pointerId);
        activePointer = null;
        slider.classList.remove('is-dragging');
        if (moved) {
          suppressClick = true;
          window.setTimeout(() => { suppressClick = false; }, 0);
        }
      };

      slider.addEventListener('pointerup', endDrag);
      slider.addEventListener('pointercancel', endDrag);
      slider.addEventListener('click', (event) => {
        if (suppressClick) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);
    });
  }

  function initProductLightboxes(root = document) {
    root.querySelectorAll('[data-product-lightbox]:not([data-initialized])').forEach((dialog) => {
      dialog.dataset.initialized = 'true';
      const section = dialog.closest('[data-section-id]');
      const track = dialog.querySelector('[data-lightbox-track]');
      const slides = Array.from(dialog.querySelectorAll('[data-lightbox-slide]'));
      const counter = dialog.querySelector('[data-lightbox-counter]');
      let currentIndex = 0;
      let returnTarget = null;
      let scrollFrame = null;

      if (!section || !track || !slides.length) return;

      const updateCounter = () => {
        if (counter) counter.textContent = `${currentIndex + 1} / ${slides.length}`;
      };

      const goTo = (index, behavior = 'smooth') => {
        currentIndex = (index + slides.length) % slides.length;
        track.scrollTo({ left: currentIndex * track.clientWidth, behavior });
        updateCounter();
      };

      const closeLightbox = () => {
        if (typeof dialog.close === 'function' && dialog.open) dialog.close();
        else dialog.removeAttribute('open');
      };

      section.querySelectorAll('[data-lightbox-open]').forEach((button) => {
        button.addEventListener('click', () => {
          returnTarget = button;
          currentIndex = Number(button.dataset.lightboxIndex) || 0;
          if (typeof dialog.showModal === 'function') dialog.showModal();
          else dialog.setAttribute('open', '');
          document.body.classList.add('lightbox-open');
          window.requestAnimationFrame(() => goTo(currentIndex, 'auto'));
        });
      });

      dialog.querySelector('[data-lightbox-close]')?.addEventListener('click', closeLightbox);
      dialog.querySelector('[data-lightbox-previous]')?.addEventListener('click', () => goTo(currentIndex - 1));
      dialog.querySelector('[data-lightbox-next]')?.addEventListener('click', () => goTo(currentIndex + 1));

      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goTo(currentIndex - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          goTo(currentIndex + 1);
        }
      });

      dialog.addEventListener('close', () => {
        document.body.classList.remove('lightbox-open');
        if (returnTarget) returnTarget.focus();
      });
      dialog.addEventListener('cancel', () => document.body.classList.remove('lightbox-open'));

      track.addEventListener('scroll', () => {
        if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(() => {
          const width = track.clientWidth || 1;
          currentIndex = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / width)));
          updateCounter();
        });
      }, { passive: true });

      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = track.scrollLeft;
        track.setPointerCapture(pointerId);
        track.classList.add('is-dragging');
      });
      track.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        event.preventDefault();
        track.scrollLeft = startScroll - (event.clientX - startX);
      });
      const endPointer = (event) => {
        if (event.pointerId !== pointerId) return;
        if (track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
        pointerId = null;
        track.classList.remove('is-dragging');
        goTo(Math.round(track.scrollLeft / (track.clientWidth || 1)));
      };
      track.addEventListener('pointerup', endPointer);
      track.addEventListener('pointercancel', endPointer);

      window.addEventListener('resize', () => {
        if (dialog.open) goTo(currentIndex, 'auto');
      });
    });
  }

  function initContentDialogs(root = document) {
    root.querySelectorAll('[data-content-dialog-open]:not([data-initialized])').forEach((trigger) => {
      trigger.dataset.initialized = 'true';
      const dialog = document.getElementById(trigger.dataset.contentDialogOpen);
      if (!dialog) return;

      if (!dialog.dataset.initialized) {
        dialog.dataset.initialized = 'true';
        dialog.querySelector('[data-content-dialog-close]')?.addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => {
          document.body.classList.remove('content-dialog-open');
          if (dialog.returnTarget) dialog.returnTarget.focus();
        });
        dialog.addEventListener('cancel', () => document.body.classList.remove('content-dialog-open'));
      }

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        dialog.returnTarget = trigger;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.body.classList.add('content-dialog-open');
        const closeButton = dialog.querySelector('[data-content-dialog-close]');
        if (closeButton) window.requestAnimationFrame(() => closeButton.focus());
      });
    });
  }

  function initInfiniteScroll(root = document) {
    root.querySelectorAll('[data-infinite-section]:not([data-initialized])').forEach((section) => {
      section.dataset.initialized = 'true';
      const grid = section.querySelector('[data-product-grid]');
      const button = section.querySelector('[data-load-more]');
      const status = section.querySelector('[data-load-status]');

      if (!grid || !button || !button.dataset.nextUrl || !('fetch' in window)) return;

      section.classList.add('infinite-scroll-active');
      let loading = false;
      let observer;

      const finish = (message) => {
        button.hidden = true;
        if (observer) observer.disconnect();
        if (status) status.textContent = message;
      };

      const loadMore = async () => {
        if (loading || !button.dataset.nextUrl) return;
        loading = true;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (status) status.textContent = button.dataset.loadingText;

        try {
          const requestUrl = new URL(button.dataset.nextUrl, window.location.origin);
          requestUrl.searchParams.set('section_id', section.dataset.sectionId);
          const response = await fetch(requestUrl.toString(), { headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const markup = await response.text();
          const parsed = new DOMParser().parseFromString(markup, 'text/html');
          const nextSection = parsed.querySelector('[data-infinite-section]');
          const nextGrid = nextSection && nextSection.querySelector('[data-product-grid]');
          if (!nextGrid) throw new Error('Product grid missing from response');

          const items = Array.from(nextGrid.children);
          items.forEach((item) => grid.appendChild(item));

          const nextButton = nextSection.querySelector('[data-load-more]');
          const nextUrl = nextButton ? nextButton.dataset.nextUrl : '';
          button.dataset.nextUrl = nextUrl;

          if (nextUrl) {
            if (status) status.textContent = button.dataset.loadedText.replace('[count]', String(items.length));
          } else {
            finish(button.dataset.completeText);
          }
        } catch (error) {
          section.classList.remove('infinite-scroll-active');
          button.hidden = true;
          if (observer) observer.disconnect();
          if (status) status.textContent = button.dataset.errorText;
        } finally {
          loading = false;
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      };

      button.addEventListener('click', loadMore);
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) loadMore();
        }, { rootMargin: '600px 0px' });
        observer.observe(button);
      }
    });
  }

  function initialize(root = document) {
    initThemeToggles(root);
    initStickyHeaders(root);
    initSlideshows(root);
    initSortForms(root);
    initMobileMenus(root);
    initProductForms(root);
    initProductSliders(root);
    initProductLightboxes(root);
    initContentDialogs(root);
    initInfiniteScroll(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
